import React, { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import AppLayout from "../../components/AppLayout";
import API_BASE_URL from "../../config/api";
import {
  Box,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Typography,
  Button,
  TextField,
  InputAdornment,
  CircularProgress,
  Avatar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import BlockIcon from "@mui/icons-material/Block";
import RestoreIcon from "@mui/icons-material/Restore";
import EventIcon from "@mui/icons-material/Event";
import PaymentIcon from "@mui/icons-material/Payment";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import { toast } from "react-toastify";
import { getInitials, getAvatarColor } from "../../utils/avatarUtils";
import ViewStaffModal from "../../components/ViewStaffModal";
import EditStaffModal from "../../components/EditStaffModal";
import DeleteConfirmDialog from "../../components/DeleteConfirmDialog";
import TerminateConfirmDialog from "../../components/TerminateConfirmDialog";

export default function Staff() {
  const [query, setQuery] = useState("");
  const [staffData, setStaffData] = useState([]);
  const [staffBalances, setStaffBalances] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [terminateDialogOpen, setTerminateDialogOpen] = useState(false);
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
  const [staffToRestore, setStaffToRestore] = useState(null);
  const [openingBalanceDialogOpen, setOpeningBalanceDialogOpen] = useState(false);
  const [selectedStaffForBalance, setSelectedStaffForBalance] = useState(null);
  const [openingBalanceForm, setOpeningBalanceForm] = useState({
    type: "opening_balance", // "opening_balance" or "advance"
    amount: "",
    effectiveDate: new Date().toISOString().split("T")[0],
    notes: "",
  });
  const navigate = useNavigate();

  // Fetch staff data from API
  useEffect(() => {
    fetchStaffData();

    // Refetch when window gains focus (user comes back to tab)
    const handleFocus = () => {
      fetchStaffData();
    };

    window.addEventListener('focus', handleFocus);

    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, []);

  const fetchStaffData = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/staff`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setStaffData(data);
        
        // Fetch balance for each staff member
        const balances = {};
        await Promise.all(
          data.map(async (staff) => {
            try {
              const balanceResponse = await fetch(`${API_BASE_URL}/api/staff/${staff.StaffId}/balance`, {
                headers: {
                  Authorization: `Bearer ${localStorage.getItem("token")}`,
                },
              });
              if (balanceResponse.ok) {
                const balanceData = await balanceResponse.json();
                balances[staff.StaffId] = balanceData;
              }
            } catch (error) {
              console.error(`Error fetching balance for staff ${staff.StaffId}:`, error);
            }
          })
        );
        setStaffBalances(balances);
      } else {
        toast.error("Failed to fetch staff data");
      }
    } catch (error) {
      console.error("Error fetching staff:", error);
      toast.error("Error loading staff data");
    } finally {
      setLoading(false);
    }
  };

  const filteredStaff = useMemo(() => {
    if (!query) return staffData;
    const q = query.toLowerCase();
    return staffData.filter((staff) =>
      staff.FullName.toLowerCase().includes(q) ||
      (staff.Designation && staff.Designation.toLowerCase().includes(q))
    );
  }, [query, staffData]);

  // Action Handlers
  const handleView = (staff) => {
    setSelectedStaff(staff);
    setViewModalOpen(true);
  };

  const handleEdit = (staff) => {
    setSelectedStaff(staff);
    setEditModalOpen(true);
  };

  const handleEditSave = async (formData) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/staff/${selectedStaff.StaffId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (response.ok) {
        toast.success('Staff updated successfully');
        fetchStaffData(); // Refresh the list
      } else {
        toast.error(data.message || 'Failed to update staff');
      }
    } catch (error) {
      console.error('Error updating staff:', error);
      toast.error('Error updating staff');
    }
  };

  const handleDelete = (staff) => {
    setSelectedStaff(staff);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/staff/${selectedStaff.StaffId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      const data = await response.json();

      if (response.ok) {
        toast.success('Staff deleted successfully');
        fetchStaffData(); // Refresh the list
      } else {
        toast.error(data.message || 'Failed to delete staff');
      }
    } catch (error) {
      console.error('Error deleting staff:', error);
      toast.error('Error deleting staff');
    }
  };

  const handleTerminate = (staff) => {
    setSelectedStaff(staff);
    setTerminateDialogOpen(true);
  };

  const handleTerminateConfirm = async ({ lastWorkingDate, terminationReason }) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/staff/${selectedStaff.StaffId}/terminate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({ lastWorkingDate, terminationReason }),
      });

      const data = await response.json();

      if (response.ok) {
        toast.success('Employee terminated successfully');
        fetchStaffData(); // Refresh the list
      } else {
        toast.error(data.message || 'Failed to terminate employee');
      }
    } catch (error) {
      console.error('Error terminating employee:', error);
      toast.error('Error terminating employee');
    }
  };

  const handleUnterminate = (staff) => {
    setStaffToRestore(staff);
    setRestoreDialogOpen(true);
  };

  const handleRestoreConfirm = async () => {
    const staff = staffToRestore;
    setRestoreDialogOpen(false);
    setStaffToRestore(null);

    try {
      const response = await fetch(`${API_BASE_URL}/api/staff/${staff.StaffId}/unterminate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      const data = await response.json();

      if (response.ok) {
        toast.success('Employee restored to active status successfully');
        fetchStaffData(); // Refresh the list
      } else {
        toast.error(data.message || 'Failed to restore employee');
      }
    } catch (error) {
      console.error('Error restoring employee:', error);
      toast.error('Error restoring employee');
    }
  };

  const handleViewAllAttendance = () => {
    navigate('/staff/attendance');
  };

  const handleViewAllSalary = () => {
    navigate('/staff/salary');
  };

  const handleViewDetails = (staff) => {
    navigate(`/admin/staff/${staff.StaffId}/attendance-salary`);
  };

  const handleOpeningBalanceClick = async (staff) => {
    setSelectedStaffForBalance(staff);
    
    // Fetch current opening balance details
    try {
      const response = await fetch(`${API_BASE_URL}/api/staff/${staff.StaffId}/opening-balance`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        const details = data.data.details;
        
        if (details) {
          // Determine type based on amount sign
          const isAdvance = details.Amount < 0;
          setOpeningBalanceForm({
            type: isAdvance ? "advance" : "opening_balance",
            amount: Math.abs(details.Amount).toString(),
            effectiveDate: details.EffectiveDate.split('T')[0],
            notes: details.Notes || "",
          });
        } else {
          // No existing opening balance
          setOpeningBalanceForm({
            type: "opening_balance",
            amount: "",
            effectiveDate: new Date().toISOString().split("T")[0],
            notes: "",
          });
        }
      } else {
        // No existing opening balance
        setOpeningBalanceForm({
          type: "opening_balance",
          amount: "",
          effectiveDate: new Date().toISOString().split("T")[0],
          notes: "",
        });
      }
    } catch (error) {
      console.error("Error fetching opening balance details:", error);
      setOpeningBalanceForm({
        type: "opening_balance",
        amount: staff.OpeningBalance ? Math.abs(staff.OpeningBalance).toString() : "",
        effectiveDate: new Date().toISOString().split("T")[0],
        notes: "",
      });
    }
    
    setOpeningBalanceDialogOpen(true);
  };

  const handleOpeningBalanceSubmit = async () => {
    if (!selectedStaffForBalance) return;

    if (!openingBalanceForm.amount || parseFloat(openingBalanceForm.amount) < 0) {
      toast.error("Please enter a valid balance amount");
      return;
    }

    try {
      const amount = parseFloat(openingBalanceForm.amount) || 0;
      
      let endpoint, body;
      
      if (openingBalanceForm.type === "opening_balance") {
        // Set opening balance (adds to staff due)
        endpoint = `${API_BASE_URL}/api/staff/${selectedStaffForBalance.StaffId}/opening-balance`;
        body = { 
          amount,
          effectiveDate: openingBalanceForm.effectiveDate,
          notes: openingBalanceForm.notes,
        };
      } else {
        // Record advance payment (subtracts from staff due)
        endpoint = `${API_BASE_URL}/api/staff/${selectedStaffForBalance.StaffId}/advance`;
        body = { 
          amount,
          effectiveDate: openingBalanceForm.effectiveDate,
          notes: openingBalanceForm.notes,
        };
      }

      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify(body),
      });

      if (response.ok) {
        toast.success(
          openingBalanceForm.type === "opening_balance" 
            ? "Opening balance updated successfully" 
            : "Advance payment recorded successfully"
        );
        fetchStaffData(); // Refresh the staff data to show updated balance
        setOpeningBalanceDialogOpen(false);
        setSelectedStaffForBalance(null);
        setOpeningBalanceForm({ 
          type: "opening_balance",
          amount: "", 
          effectiveDate: new Date().toISOString().split("T")[0],
          notes: "" 
        });
      } else {
        toast.error(
          openingBalanceForm.type === "opening_balance"
            ? "Failed to update opening balance"
            : "Failed to record advance payment"
        );
      }
    } catch (error) {
      console.error("Error updating opening balance:", error);
      toast.error("Failed to update opening balance");
    }
  };

  const handleDeleteBalance = async () => {
    if (!selectedStaffForBalance) return;

    if (!window.confirm("Are you sure you want to delete this balance entry? This will reset the balance to zero.")) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/staff/${selectedStaffForBalance.StaffId}/balance`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      if (response.ok) {
        toast.success("Balance entry deleted successfully");
        fetchStaffData(); // Refresh the staff data
        setOpeningBalanceDialogOpen(false);
        setSelectedStaffForBalance(null);
        setOpeningBalanceForm({ 
          type: "opening_balance",
          amount: "", 
          effectiveDate: new Date().toISOString().split("T")[0],
          notes: "" 
        });
      } else {
        const data = await response.json();
        toast.error(data.message || "Failed to delete balance entry");
      }
    } catch (error) {
      console.error("Error deleting balance:", error);
      toast.error("Failed to delete balance entry");
    }
  };

  // Legacy function for backward compatibility
  const handleDeleteOpeningBalance = handleDeleteBalance;

  return (
    <AppLayout>
      <Box
        sx={{
          minHeight: "calc(100vh - 80px)",
          background: "linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)",
          p: { xs: 2, md: 4 },
        }}
      >
        {/* Title */}
        <Typography
          variant="h5"
          sx={{
            fontWeight: 500,
            letterSpacing: 3,
            color: "#BC965E",
            textAlign: "center",
            mb: 4,
            textDecoration: "underline",
            textUnderlineOffset: 12,
            textDecorationThickness: 2,
            fontSize: { xs: "1.25rem", md: "1.75rem" },
          }}
        >
          STAFF
        </Typography>

        {/* Search & Navigation Section */}
        <Box sx={{ maxWidth: 1300, mx: "auto", mb: 4 }}>
          <Box
            sx={{
              display: "flex",
              gap: 2,
              mb: 3,
              flexDirection: { xs: "column", sm: "row" },
              alignItems: { xs: "stretch", sm: "center" },
            }}
          >
            <TextField
              fullWidth
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by name or designation..."
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: "#C4A574" }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                flex: 1,
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "#3a3a3a",
                  borderRadius: 2,
                  border: "1px solid rgba(196, 165, 116, 0.2)",
                  color: "#C4A574",
                  "& fieldset": {
                    borderColor: "transparent",
                  },
                  "&:hover fieldset": {
                    borderColor: "rgba(196, 165, 116, 0.3)",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#C4A574",
                  },
                },
                "& .MuiOutlinedInput-input": {
                  color: "#C4A574",
                  "&::placeholder": {
                    color: "rgba(196, 165, 116, 0.6)",
                    opacity: 1,
                  },
                },
              }}
            />

            <Box
              sx={{
                display: "flex",
                gap: 2,
                flexDirection: { xs: "column", sm: "row" },
              }}
            >
              <Button
                variant="contained"
                startIcon={<EventIcon />}
                onClick={handleViewAllAttendance}
                sx={{
                  bgcolor: "#C4A574",
                  color: "#000",
                  fontWeight: 700,
                  px: { xs: 2, sm: 3 },
                  py: 1.2,
                  borderRadius: 1,
                  fontSize: { xs: "0.85rem", sm: "1rem" },
                  minWidth: { xs: "100%", sm: "auto" },
                  "&:hover": { bgcolor: "#B39565" },
                }}
              >
                Attendance
              </Button>
              <Button
                variant="contained"
                startIcon={<PaymentIcon />}
                onClick={handleViewAllSalary}
                sx={{
                  bgcolor: "#C4A574",
                  color: "#000",
                  fontWeight: 700,
                  px: { xs: 2, sm: 3 },
                  py: 1.2,
                  borderRadius: 1,
                  fontSize: { xs: "0.85rem", sm: "1rem" },
                  minWidth: { xs: "100%", sm: "auto" },
                  "&:hover": { bgcolor: "#B39565" },
                }}
              >
                Salary
              </Button>
            </Box>
          </Box>

          {/* Staff Cards Grid */}
          <Grid container spacing={3} sx={{justifyContent: "center",}}>
            {loading ? (
              <Grid item xs={12}>
                <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                  <CircularProgress sx={{ color: "#C4A574" }} />
                </Box>
              </Grid>
            ) : filteredStaff && filteredStaff.length > 0 ? (
              filteredStaff.map((staff) => {
                const initials = getInitials(staff.FullName);
                const avatarColor = getAvatarColor(staff.FullName);

                return (
                  <Grid item key={staff.StaffId} xs={12} sm={6} md={4} lg={3}>
                    <Card
                      sx={{
                        borderRadius: 2,
                        overflow: "hidden",
                        transition: "all 0.3s ease",
                        backgroundColor: "#2a2a2a",
                        border: "1px solid rgba(196, 165, 116, 0.2)",
                        "&:hover": {
                          transform: "translateY(-8px)",
                          boxShadow: "0 12px 32px rgba(0,0,0,0.4)",
                          borderColor: "#C4A574",
                        },
                      }}
                    >
                      {/* Avatar Section */}
                      <Box
                        sx={{
                          bgcolor: "rgba(0, 0, 0, 0.3)",
                          p: { xs: 2, sm: 3 },
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                        }}
                      >
                        {staff.Photo ? (
                          <Box
                            component="img"
                            src={`${API_BASE_URL}${staff.Photo}`}
                            alt={staff.FullName}
                            sx={{
                              width: 100,
                              height: 100,
                              borderRadius: "50%",
                              objectFit: "cover",
                              border: "3px solid #C4A574",
                            }}
                          />
                        ) : (
                          <Avatar
                            sx={{
                              width: 100,
                              height: 100,
                              bgcolor: avatarColor,
                              fontSize: "2.5rem",
                              fontWeight: 700,
                              border: "3px solid #C4A574",
                            }}
                          >
                            {initials}
                          </Avatar>
                        )}
                      </Box>

                      {/* Content Section */}
                      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                        {/* Name */}
                        <Typography
                          variant="h6"
                          sx={{
                            color: "#fff",
                            fontWeight: 700,
                            mb: 0.5,
                            fontSize: { xs: "1rem", sm: "1.1rem" },
                            textAlign: "center",
                          }}
                        >
                          {staff.FullName}
                        </Typography>

                        {/* Designation */}
                        <Typography
                          variant="body2"
                          sx={{
                            color: "#C4A574",
                            fontWeight: 600,
                            textAlign: "center",
                            mb: 0.5,
                            fontSize: { xs: "0.8rem", sm: "0.9rem" },
                          }}
                        >
                          {staff.Designation || "Staff"}
                        </Typography>

                        {/* Staff Type Badge */}
                        <Box
                          sx={{
                            textAlign: "center",
                            mb: 1,
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{
                              display: "inline-block",
                              px: 1,
                              py: 0.25,
                              borderRadius: 0.5,
                              bgcolor: staff.StaffType === "Hourly" 
                                ? "rgba(33, 150, 243, 0.15)" 
                                : "rgba(156, 39, 176, 0.15)",
                              color: staff.StaffType === "Hourly" 
                                ? "#2196F3" 
                                : "#9C27B0",
                              fontWeight: 600,
                              fontSize: { xs: "0.65rem", sm: "0.75rem" },
                              textTransform: "uppercase",
                              letterSpacing: "0.5px",
                            }}
                          >
                            {staff.StaffType || "Task-Based"}
                          </Typography>
                        </Box>

                        {/* Salary Info */}
                        <Typography
                          variant="caption"
                          sx={{
                            color: "#999",
                            display: "block",
                            textAlign: "center",
                            mb: 1.5,
                            fontSize: { xs: "0.75rem", sm: "0.85rem" },
                          }}
                        >
                          {staff.StaffType === "Hourly" 
                            ? `₹${(staff.MonthlySalary || 0).toLocaleString()}/month (Hourly)`
                            : `₹${(staff.MonthlySalary || 0).toLocaleString()}/month + Tasks`
                          }
                        </Typography>

                        {/* Balance Display */}
                        {staffBalances[staff.StaffId] &&
                          staffBalances[staff.StaffId].balance !== 0 && (
                            <Box
                              sx={{
                                textAlign: "center",
                                mb: 2,
                                px: 1,
                                py: 0.75,
                                borderRadius: 1,
                                bgcolor: staffBalances[staff.StaffId].isDue
                                  ? "rgba(255, 107, 107, 0.15)"
                                  : "rgba(76, 175, 80, 0.15)",
                              }}
                            >
                              <Typography
                                variant="caption"
                                sx={{
                                  color: staffBalances[staff.StaffId].isDue
                                    ? "#ff6b6b"
                                    : "#4caf50",
                                  fontWeight: 600,
                                  fontSize: { xs: "0.7rem", sm: "0.8rem" },
                                }}
                              >
                                {staffBalances[staff.StaffId].isDue
                                  ? "Due: "
                                  : "Advance: "}
                                ₹
                                {Math.abs(
                                  staffBalances[staff.StaffId].balance
                                ).toLocaleString("en-IN")}
                              </Typography>
                            </Box>
                          )}

                        {/* Main Action Button - Different text based on staff type */}
                        <Button
                          fullWidth
                          variant="contained"
                          onClick={() => handleViewDetails(staff)}
                          sx={{
                            bgcolor: "#C4A574",
                            color: "#000",
                            fontWeight: 700,
                            py: { xs: 0.8, sm: 1 },
                            mb: 1.5,
                            borderRadius: 1,
                            fontSize: { xs: "0.8rem", sm: "0.9rem" },
                            "&:hover": { bgcolor: "#B39565" },
                          }}
                        >
                          {staff.StaffType === "Hourly" ? "Attendance & Salary" : "Salary Details"}
                        </Button>

                        {/* Action Buttons Grid - Row 1 */}
                        <Box
                          sx={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr 1fr",
                            gap: 1,
                            mb: 1,
                          }}
                        >
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={
                              <VisibilityIcon sx={{ fontSize: "0.85rem" }} />
                            }
                            onClick={() => handleView(staff)}
                            sx={{
                              borderColor: "#C4A574",
                              color: "#C4A574",
                              fontWeight: 600,
                              fontSize: { xs: "0.65rem", sm: "0.75rem" },
                              py: { xs: 0.4, sm: 0.6 },
                              px: { xs: 0.3, sm: 0.5 },
                              "&:hover": {
                                bgcolor: "rgba(196, 165, 116, 0.1)",
                                borderColor: "#C4A574",
                              },
                            }}
                          >
                            View
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<EditIcon sx={{ fontSize: "0.85rem" }} />}
                            onClick={() => handleEdit(staff)}
                            sx={{
                              borderColor: "#C4A574",
                              color: "#C4A574",
                              fontWeight: 600,
                              fontSize: { xs: "0.65rem", sm: "0.75rem" },
                              py: { xs: 0.4, sm: 0.6 },
                              px: { xs: 0.3, sm: 0.5 },
                              "&:hover": {
                                bgcolor: "rgba(196, 165, 116, 0.1)",
                                borderColor: "#C4A574",
                              },
                            }}
                          >
                            Edit
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<DeleteIcon sx={{ fontSize: "0.85rem" }} />}
                            onClick={() => handleDelete(staff)}
                            sx={{
                              borderColor: "#ff6b6b",
                              color: "#ff6b6b",
                              fontWeight: 600,
                              fontSize: { xs: "0.65rem", sm: "0.75rem" },
                              py: { xs: 0.4, sm: 0.6 },
                              px: { xs: 0.3, sm: 0.5 },
                              "&:hover": {
                                bgcolor: "rgba(255, 107, 107, 0.1)",
                                borderColor: "#ff6b6b",
                              },
                            }}
                          >
                            Delete
                          </Button>
                        </Box>

                        {/* Action Buttons Grid - Row 2 */}
                        <Box
                          sx={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr",
                            gap: 1,
                            mb: 1,
                          }}
                        >
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={
                              <AccountBalanceIcon sx={{ fontSize: "0.85rem" }} />
                            }
                            onClick={() => handleOpeningBalanceClick(staff)}
                            sx={{
                              borderColor: "#C4A574",
                              color: "#C4A574",
                              fontWeight: 600,
                              fontSize: { xs: "0.6rem", sm: "0.7rem" },
                              py: { xs: 0.4, sm: 0.6 },
                              px: { xs: 0.3, sm: 0.5 },
                              "&:hover": {
                                bgcolor: "rgba(196, 165, 116, 0.1)",
                                borderColor: "#C4A574",
                              },
                            }}
                          >
                            Opening Balance
                          </Button>

                          {staff.Status === "Active" && (
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<BlockIcon sx={{ fontSize: "0.85rem" }} />}
                              onClick={() => handleTerminate(staff)}
                              sx={{
                                borderColor: "#ff9800",
                                color: "#ff9800",
                                fontWeight: 600,
                                fontSize: { xs: "0.6rem", sm: "0.7rem" },
                                py: { xs: 0.4, sm: 0.6 },
                                px: { xs: 0.3, sm: 0.5 },
                                "&:hover": {
                                  bgcolor: "rgba(255, 152, 0, 0.1)",
                                  borderColor: "#ff9800",
                                },
                              }}
                            >
                              Terminate
                            </Button>
                          )}
                          {staff.Status === "Terminated" && (
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<RestoreIcon sx={{ fontSize: "0.85rem" }} />}
                              onClick={() => handleUnterminate(staff)}
                              sx={{
                                borderColor: "#4caf50",
                                color: "#4caf50",
                                fontWeight: 600,
                                fontSize: { xs: "0.6rem", sm: "0.7rem" },
                                py: { xs: 0.4, sm: 0.6 },
                                px: { xs: 0.3, sm: 0.5 },
                                "&:hover": {
                                  bgcolor: "rgba(76, 175, 80, 0.1)",
                                  borderColor: "#4caf50",
                                },
                              }}
                            >
                              Restore
                            </Button>
                          )}
                        </Box>

                        {/* Status Badge */}
                        <Box
                          sx={{
                            textAlign: "center",
                            mt: 1.5,
                            pt: 1.5,
                            borderTop: "1px solid rgba(196, 165, 116, 0.2)",
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{
                              display: "inline-block",
                              px: 1.5,
                              py: 0.5,
                              borderRadius: 1,
                              bgcolor:
                                staff.Status === "Active"
                                  ? "rgba(76, 175, 80, 0.15)"
                                  : "rgba(255, 152, 0, 0.15)",
                              color:
                                staff.Status === "Active" ? "#4caf50" : "#ff9800",
                              fontWeight: 700,
                              fontSize: { xs: "0.7rem", sm: "0.8rem" },
                            }}
                          >
                            {staff.Status || "Active"}
                          </Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })
            ) : (
              <Grid item xs={12}>
                <Typography
                  align="center"
                  sx={{
                    color: "#999",
                    py: 6,
                    fontSize: { xs: "1rem", sm: "1.1rem" },
                  }}
                >
                  No staff members found.
                </Typography>
              </Grid>
            )}
          </Grid>
        </Box>

        {/* Decorative Fade */}
        <Box
          sx={{
            mt: 6,
            height: 60,
            background: "linear-gradient(180deg, rgba(0,0,0,0), rgba(0,0,0,0.3))",
            borderRadius: 2,
          }}
        />
      </Box>

      {/* Modals */}
      <ViewStaffModal
        open={viewModalOpen}
        staff={selectedStaff}
        onClose={() => setViewModalOpen(false)}
      />

      <EditStaffModal
        open={editModalOpen}
        staff={selectedStaff}
        onClose={() => setEditModalOpen(false)}
        onSave={handleEditSave}
      />

      <DeleteConfirmDialog
        open={deleteDialogOpen}
        staff={selectedStaff}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteConfirm}
      />

      <TerminateConfirmDialog
        open={terminateDialogOpen}
        staff={selectedStaff}
        onClose={() => setTerminateDialogOpen(false)}
        onConfirm={handleTerminateConfirm}
      />

      {/* Restore Confirmation Dialog */}
      <Dialog
        open={restoreDialogOpen}
        onClose={() => { setRestoreDialogOpen(false); setStaffToRestore(null); }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            p: 1,
            bgcolor: '#2a2a2a',
            color: '#fff',
          },
        }}
      >
        <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
            <RestoreIcon sx={{ fontSize: { xs: 40, sm: 48 }, color: '#2e7d32' }} />
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#fff', fontSize: { xs: '1.2rem', sm: '1.5rem' } }}>
              Restore Employee
            </Typography>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ textAlign: 'center', py: 2 }}>
          <Typography variant="body1" sx={{ color: '#ccc', lineHeight: 1.6 }}>
            Are you sure you want to restore{' '}
            <Box component="span" sx={{ color: '#C4A574', fontWeight: 700 }}>
              {staffToRestore?.FullName}
            </Box>{' '}
            to active status?
          </Typography>
          <Typography variant="body2" sx={{ color: '#888', mt: 1 }}>
            They will regain full access and appear as an active employee.
          </Typography>
        </DialogContent>

        <DialogActions sx={{ justifyContent: 'center', gap: 2, pb: 3, flexWrap: 'wrap' }}>
          <Button
            onClick={() => { setRestoreDialogOpen(false); setStaffToRestore(null); }}
            variant="outlined"
            sx={{
              minWidth: 100,
              color: '#ccc',
              borderColor: '#555',
              '&:hover': { borderColor: '#777', bgcolor: 'rgba(255,255,255,0.05)' },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleRestoreConfirm}
            variant="contained"
            startIcon={<RestoreIcon />}
            sx={{
              minWidth: 100,
              bgcolor: '#2e7d32',
              color: '#fff',
              fontWeight: 600,
              '&:hover': { bgcolor: '#1b5e20' },
            }}
          >
            Restore
          </Button>
        </DialogActions>
      </Dialog>

      {/* Opening Balance/Advance Dialog */}
      <Dialog
        open={openingBalanceDialogOpen}
        onClose={() => setOpeningBalanceDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: "#2a2a2a",
            backgroundImage: "none",
          },
        }}
      >
        <DialogTitle
          sx={{
            bgcolor: "#C4A574",
            color: "#000",
            fontWeight: 700,
            fontSize: { xs: "1rem", sm: "1.25rem" },
          }}
        >
          {selectedStaffForBalance && staffBalances[selectedStaffForBalance.StaffId] && 
           (staffBalances[selectedStaffForBalance.StaffId].isDue || staffBalances[selectedStaffForBalance.StaffId].isAdvance)
            ? "Edit Balance Entry" : "Set Opening Balance / Advance"}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ pt: 1 }}>
            {selectedStaffForBalance && (
              <Box
                sx={{
                  mb: 3,
                  p: { xs: 1.5, sm: 2 },
                  bgcolor: "#3a3a3a",
                  border: "1px solid rgba(196, 165, 116, 0.2)",
                  borderRadius: 1,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: "#C4A574",
                    fontWeight: 700,
                    mb: 1,
                    fontSize: { xs: "0.8rem", sm: "0.9rem" },
                  }}
                >
                  <strong>Staff:</strong> {selectedStaffForBalance.FullName}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: "#999",
                    fontSize: { xs: "0.75rem", sm: "0.85rem" },
                  }}
                >
                  <strong>Staff Code:</strong> {selectedStaffForBalance.Username}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: "#999",
                    fontSize: { xs: "0.75rem", sm: "0.85rem" },
                  }}
                >
                  <strong>Current Balance:</strong> {(() => {
                    const balance = staffBalances[selectedStaffForBalance.StaffId];
                    if (balance) {
                      const amount = Math.abs(balance.balance);
                      const type = balance.isDue ? " (Due)" : balance.isAdvance ? " (Advance)" : "";
                      return `₹${amount.toLocaleString()}${type}`;
                    }
                    return "₹0";
                  })()}
                </Typography>
                {/* Show existing balance info */}
                {selectedStaffForBalance.OpeningBalance && parseFloat(selectedStaffForBalance.OpeningBalance) !== 0 && (
                  <Typography variant="caption" sx={{ color: "#666", fontStyle: "italic", display: "block", mt: 1 }}>
                    You can edit the amount, switch between types, or delete the entry completely.
                  </Typography>
                )}
              </Box>
            )}
            
            <FormControl component="fieldset" sx={{ mb: 3 }}>
              <FormLabel component="legend" sx={{ fontWeight: 600, color: "#C4A574" }}>
                Select Type
              </FormLabel>
              <RadioGroup
                value={openingBalanceForm.type}
                onChange={(e) => {
                  const newType = e.target.value;
                  let newAmount = openingBalanceForm.amount; // Keep current amount when switching
                  
                  setOpeningBalanceForm({ ...openingBalanceForm, type: newType, amount: newAmount });
                }}
                row
              >
                <FormControlLabel 
                  value="opening_balance" 
                  control={<Radio sx={{ color: "#C4A574", "&.Mui-checked": { color: "#C4A574" } }} />} 
                  label={<Typography sx={{ color: "#fff", fontSize: { xs: "0.8rem", sm: "0.9rem" } }}>Opening Balance (Add to Due)</Typography>}
                />
                <FormControlLabel 
                  value="advance" 
                  control={<Radio sx={{ color: "#C4A574", "&.Mui-checked": { color: "#C4A574" } }} />} 
                  label={<Typography sx={{ color: "#fff", fontSize: { xs: "0.8rem", sm: "0.9rem" } }}>Advance (Subtract from Due)</Typography>}
                />
              </RadioGroup>
            </FormControl>
            
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label="Amount (₹)"
                  type="number"
                  value={openingBalanceForm.amount}
                  onChange={(e) => setOpeningBalanceForm({ ...openingBalanceForm, amount: e.target.value })}
                  fullWidth
                  required
                  slotProps={{ htmlInput: { step: 0.01, min: 0 } }}
                  helperText={
                    openingBalanceForm.type === "opening_balance" 
                      ? "This amount will be added to the staff's due amount. Any existing advance will be cleared."
                      : "This amount will be subtracted from the staff's due amount. Any existing opening balance will be cleared."
                  }
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "#3a3a3a",
                      color: "#fff",
                      "& fieldset": {
                        borderColor: "rgba(196, 165, 116, 0.3)",
                      },
                      "&:hover fieldset": {
                        borderColor: "rgba(196, 165, 116, 0.5)",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#C4A574",
                      },
                    },
                    "& .MuiInputLabel-root": {
                      color: "#C4A574",
                      "&.Mui-focused": {
                        color: "#C4A574",
                      },
                    },
                    "& .MuiFormHelperText-root": {
                      color: "#999",
                      fontSize: { xs: "0.7rem", sm: "0.75rem" },
                    },
                  }}
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  label="Effective Date"
                  type="date"
                  value={openingBalanceForm.effectiveDate}
                  onChange={(e) => setOpeningBalanceForm({ ...openingBalanceForm, effectiveDate: e.target.value })}
                  fullWidth
                  required
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "#3a3a3a",
                      color: "#fff",
                      "& fieldset": {
                        borderColor: "rgba(196, 165, 116, 0.3)",
                      },
                      "&:hover fieldset": {
                        borderColor: "rgba(196, 165, 116, 0.5)",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#C4A574",
                      },
                    },
                    "& .MuiInputLabel-root": {
                      color: "#C4A574",
                      "&.Mui-focused": {
                        color: "#C4A574",
                      },
                    },
                  }}
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  label="Notes"
                  value={openingBalanceForm.notes}
                  onChange={(e) => setOpeningBalanceForm({ ...openingBalanceForm, notes: e.target.value })}
                  fullWidth
                  multiline
                  rows={3}
                  placeholder="Add notes about this balance entry..."
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "#3a3a3a",
                      color: "#fff",
                      "& fieldset": {
                        borderColor: "rgba(196, 165, 116, 0.3)",
                      },
                      "&:hover fieldset": {
                        borderColor: "rgba(196, 165, 116, 0.5)",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#C4A574",
                      },
                    },
                    "& .MuiInputLabel-root": {
                      color: "#C4A574",
                      "&.Mui-focused": {
                        color: "#C4A574",
                      },
                    },
                    "& .MuiOutlinedInput-input::placeholder": {
                      color: "rgba(255, 255, 255, 0.5)",
                      opacity: 1,
                    },
                  }}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, justifyContent: "space-between" }}>
          <Box>
            {/* Show delete button only if there's an existing balance entry */}
            {selectedStaffForBalance && staffBalances[selectedStaffForBalance.StaffId] && 
             (staffBalances[selectedStaffForBalance.StaffId].isDue || staffBalances[selectedStaffForBalance.StaffId].isAdvance) && (
              <Button 
                onClick={handleDeleteBalance}
                variant="outlined"
                startIcon={<DeleteIcon />}
                sx={{ 
                  color: "#ff6b6b", 
                  borderColor: "#ff6b6b",
                  "&:hover": { 
                    bgcolor: "rgba(255, 107, 107, 0.1)",
                    borderColor: "#ff6b6b"
                  }
                }}
              >
                Delete Entry
              </Button>
            )}
          </Box>
          
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button 
              onClick={() => setOpeningBalanceDialogOpen(false)} 
              sx={{ color: "#999" }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleOpeningBalanceSubmit} 
              variant="contained" 
              sx={{ bgcolor: "#C4A574", color: "#000", fontWeight: 700, "&:hover": { bgcolor: "#B39565" } }}
            >
              {((selectedStaffForBalance && staffBalances[selectedStaffForBalance.StaffId] && 
                 (staffBalances[selectedStaffForBalance.StaffId].isDue || staffBalances[selectedStaffForBalance.StaffId].isAdvance))) 
                ? "Update Balance" 
                : (openingBalanceForm.type === "opening_balance" ? "Set Opening Balance" : "Record Advance")}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </AppLayout>
  );
}
